import type { AdminAppliedPromo, AdminCreditEntry, AdminOrgBilling } from '@/api/adminClient';
import { formatDate, formatUsd } from '@/lib/format';
import { humanizeKey, type StatusTone } from '@/lib/runTelemetry';

export type MoneyPill = { tone: StatusTone; label: string };

function subscriptionTone(status?: string): StatusTone {
  if (status === 'active' || status === 'trialing') return 'success';
  if (status === 'past_due' || status === 'unpaid' || status === 'incomplete') return 'warning';
  if (status === 'canceled' || status === 'incomplete_expired') return 'danger';
  return 'neutral';
}

/** "Nov 2": a card line has no room for the year. */
export function formatShortDate(iso: string | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(date);
}

/** "$500" for whole dollars, "$12.50" otherwise. */
export function formatCentsCompact(cents: number | undefined): string {
  const value = (cents ?? 0) / 100;
  if (Number.isInteger(value)) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
  }
  return formatUsd(value);
}

const STATUS_LABELS: Record<string, string> = {
  active: 'Active',
  past_due: 'Past due',
  unpaid: 'Unpaid',
  incomplete: 'Payment not finished',
  incomplete_expired: 'Checkout expired',
  canceled: 'Canceled',
  paused: 'Paused',
};

export function planStatusPill(billing?: AdminOrgBilling): MoneyPill {
  const status = billing?.status;
  if (!status) return { tone: 'neutral', label: 'Not subscribed' };
  if (status === 'trialing') {
    const ends = formatShortDate(billing.trialEnd);
    return { tone: 'success', label: ends ? `Trial · ends ${ends}` : 'Trial' };
  }
  if (status === 'active' && billing.cancelAtPeriodEnd) {
    const ends = formatShortDate(billing.currentPeriodEnd);
    return { tone: 'warning', label: ends ? `Cancels ${ends}` : 'Cancels at period end' };
  }
  return { tone: subscriptionTone(status), label: STATUS_LABELS[status] ?? humanizeKey(status) };
}

/** When the plan next renews or stops, or nothing when there is no period. */
export function planPeriodLine(billing?: AdminOrgBilling): string | undefined {
  if (!billing?.status || billing.status === 'trialing' || billing.status === 'canceled') return undefined;
  const date = formatShortDate(billing.currentPeriodEnd);
  if (!date || billing.cancelAtPeriodEnd) return undefined;
  return `Renews ${date}`;
}

function describePromo(promo?: AdminAppliedPromo): string {
  if (!promo) return 'None';
  if (promo.type === 'percent_off' && typeof promo.percentOff === 'number') return `${promo.percentOff}% off`;
  if (promo.type === 'amount_off' && typeof promo.amountOffCents === 'number') {
    return `${formatUsd(promo.amountOffCents / 100)} off`;
  }
  if (promo.type === 'trial' && promo.endsAt) return `Trial until ${formatDate(promo.endsAt)}`;
  return 'Applied';
}

/** "20% off · code SAVE20", or "No promo". */
export function promoSummary(promo?: AdminAppliedPromo): string {
  if (!promo) return 'No promo';
  const parts = [describePromo(promo)];
  if (promo.name) parts.push(promo.name);
  if (promo.code) parts.push(`code ${promo.code}`);
  return parts.join(' · ');
}

/** "$500 left of $1,000", or that no credit was ever granted. */
export function creditsSummary(billing?: AdminOrgBilling): string {
  if (!billing) return '—';
  if (!billing.creditGrantedCents) return 'No credit granted';
  return `${formatCentsCompact(billing.creditBalanceCents)} left of ${formatCentsCompact(billing.creditGrantedCents)}`;
}

/** The newest grant in a newest-first page of credit history. */
export function latestGrant(entries: AdminCreditEntry[]): AdminCreditEntry | undefined {
  return entries.find((entry) => entry.type === 'grant');
}

export function lastGrantLine(entry?: AdminCreditEntry): string | undefined {
  if (!entry) return undefined;
  const parts = [`Last grant ${formatCentsCompact(Math.abs(entry.amountCents))}`];
  const date = formatShortDate(entry.createdAt);
  if (date) parts[0] += ` on ${date}`;
  const reason = entry.reason?.trim();
  if (reason) parts.push(reason);
  return parts.join(' · ');
}

/** "2 paid · none open", or how many are open and what they owe. */
export function invoicesSummary(invoices: AdminOrgBilling['invoices'] = []): string {
  if (invoices.length === 0) return 'No invoices yet';
  const paid = invoices.filter((invoice) => invoice.status === 'paid').length;
  const open = invoices.filter((invoice) => invoice.status === 'open');
  if (open.length === 0) return `${paid} paid · none open`;
  const due = open.reduce((sum, invoice) => sum + (invoice.amountDue ?? 0), 0);
  return `${paid} paid · ${open.length} open, ${formatCentsCompact(due)} due`;
}

export function invoicesPill(invoices: AdminOrgBilling['invoices'] = []): MoneyPill | undefined {
  return invoices.some((invoice) => invoice.status === 'open') ? { tone: 'warning', label: 'Payment due' } : undefined;
}
